/**
 * Engage Settings
 *
 * Configuration for Engage features, presets, and webhooks.
 */

import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { api, useAuthStore } from '@personic/shared'
import { usePragmaticSettings } from '@/hooks/usePragmatic'
import StreamModeIndicator from '@/components/engage/StreamModeIndicator'
import { usePersonaEngineConfig, usePersonaEngineEngines, useUpdatePersonaEngineConfig } from '@/hooks/usePersonaEngine'
import EngineSelector from '@/components/persona-engine/EngineSelector'
import ModifierPanel from '@/components/persona-engine/ModifierPanel'
import ThresholdSliders from '@/components/persona-engine/ThresholdSliders'
import type { ModifierType, EngineType, InjectionProfile } from '@personic/shared/types/persona-engine'

interface CalibrationPreset {
  id: string
  name: string
  slug: string
  description?: string
  category: string
  is_system: boolean
  is_active: boolean
  calibration: Record<string, unknown>
}

interface Webhook {
  id: string
  name: string
  url: string
  events: string[]
  is_active: boolean
  failure_count: number
  last_success_at?: string
  last_failure_at?: string
  created_at: string
}

export default function EngageSettings() {
  const [activeTab, setActiveTab] = useState<'presets' | 'webhooks' | 'general' | 'pragmatic' | 'persona-engine'>('presets')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Engage Settings</h1>
        <p className="text-gray-600 mt-1">
          Configure Engage behavior, calibration presets, and integrations
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {[
            { id: 'presets', label: 'Calibration Presets', icon: '🎛️' },
            { id: 'webhooks', label: 'Webhooks', icon: '🔗' },
            { id: 'pragmatic', label: 'Pragmatic Intelligence', icon: '🧠' },
            { id: 'persona-engine', label: 'Persona Engine', icon: '🧬' },
            { id: 'general', label: 'General', icon: '⚙️' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as 'presets' | 'webhooks' | 'general' | 'pragmatic' | 'persona-engine')}
              className={`flex items-center gap-2 pb-4 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-engage-primary text-engage-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {activeTab === 'presets' && <PresetsTab />}
        {activeTab === 'webhooks' && <WebhooksTab />}
        {activeTab === 'pragmatic' && <PragmaticQuickSettings />}
        {activeTab === 'general' && <GeneralTab />}
        {activeTab === 'persona-engine' && <PersonaEngineTab />}
      </motion.div>
    </div>
  )
}

function PresetsTab() {
  const { data: presets, isLoading } = useQuery<CalibrationPreset[]>({
    queryKey: ['engage-calibration-presets'],
    queryFn: async () => {
      const response = await api.get('/api/engage/v1/calibration/presets')
      return response.data
    },
  })

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'call_center': return '📞'
      case 'sales': return '💼'
      case 'technical': return '🔧'
      case 'hospitality': return '🏨'
      case 'healthcare': return '🏥'
      case 'financial': return '💰'
      case 'ai_agent': return '🤖'
      default: return '⚙️'
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-gray-600">
          Calibration presets optimize Engage for different use cases.
        </p>
        <button className="px-4 py-2 bg-engage-primary text-white rounded-lg text-sm font-medium hover:bg-engage-primary/90 transition-colors">
          Create Custom Preset
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading presets...</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {presets?.map((preset) => (
            <div
              key={preset.id}
              className="card p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="text-3xl">{getCategoryIcon(preset.category)}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{preset.name}</h3>
                    {preset.is_system && (
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                        System
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{preset.description}</p>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs text-gray-400">
                      Category: {preset.category}
                    </span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs font-mono text-gray-400">
                      {preset.slug}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function WebhooksTab() {
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newWebhook, setNewWebhook] = useState({
    name: '',
    url: '',
    events: [] as string[],
  })

  const { data: webhooks, isLoading } = useQuery<Webhook[]>({
    queryKey: ['engage-webhooks'],
    queryFn: async () => {
      const response = await api.get('/api/engage/v1/webhooks')
      return response.data
    },
  })

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; url: string; events: string[] }) => {
      const response = await api.post('/api/engage/v1/webhooks', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engage-webhooks'] })
      setShowCreateModal(false)
      setNewWebhook({ name: '', url: '', events: [] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (webhookId: string) => {
      await api.delete(`/api/engage/v1/webhooks/${webhookId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engage-webhooks'] })
    },
  })

  const eventTypes = [
    { value: 'engage.session.started', label: 'Session Started' },
    { value: 'engage.session.ended', label: 'Session Ended' },
    { value: 'engage.condition.detected', label: 'Condition Detected' },
    { value: 'engage.guidance.generated', label: 'Guidance Generated' },
    { value: 'engage.persona.generated', label: 'Persona Generated' },
    { value: 'engage.relationship.created', label: 'Relationship Created' },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-gray-600">
          Receive real-time notifications when events occur in Engage.
        </p>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-engage-primary text-white rounded-lg text-sm font-medium hover:bg-engage-primary/90 transition-colors"
        >
          Create Webhook
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading webhooks...</div>
      ) : webhooks?.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="text-5xl mb-4">🔗</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Webhooks</h3>
          <p className="text-gray-500 mb-6">
            Create a webhook to receive real-time event notifications.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-engage-primary text-white rounded-lg text-sm font-medium"
          >
            Create Your First Webhook
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {webhooks?.map((webhook) => (
            <div key={webhook.id} className="card p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${webhook.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <div>
                    <h3 className="font-semibold text-gray-900">{webhook.name}</h3>
                    <p className="text-sm text-gray-500 font-mono truncate max-w-md">
                      {webhook.url}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => deleteMutation.mutate(webhook.id)}
                    className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {webhook.events.map((event) => (
                  <span
                    key={event}
                    className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded"
                  >
                    {event.replace('engage.', '')}
                  </span>
                ))}
              </div>
              {webhook.failure_count > 0 && (
                <div className="mt-2 text-xs text-amber-600">
                  {webhook.failure_count} recent failures
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create Webhook</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newWebhook.name}
                  onChange={(e) => setNewWebhook({ ...newWebhook, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  placeholder="My Webhook"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                <input
                  type="url"
                  value={newWebhook.url}
                  onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg font-mono text-sm"
                  placeholder="https://your-server.com/webhooks/engage"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Events</label>
                <div className="space-y-2">
                  {eventTypes.map((event) => (
                    <label key={event.value} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newWebhook.events.includes(event.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewWebhook({ ...newWebhook, events: [...newWebhook.events, event.value] })
                          } else {
                            setNewWebhook({ ...newWebhook, events: newWebhook.events.filter(ev => ev !== event.value) })
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">{event.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => createMutation.mutate(newWebhook)}
                disabled={!newWebhook.name || !newWebhook.url || newWebhook.events.length === 0}
                className="px-4 py-2 bg-engage-primary text-white rounded-lg disabled:opacity-50"
              >
                Create Webhook
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

function PragmaticQuickSettings() {
  const { user } = useAuthStore()
  const profileId = user?.id || ''
  const { data: settings, isLoading } = usePragmaticSettings(profileId)

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading pragmatic settings...</div>
  }

  return (
    <div className="space-y-6">
      <p className="text-gray-600">
        Quick view of your Pragmatic Intelligence configuration.
      </p>

      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Current Configuration</h3>
          <Link
            to="/tenant/engage/pragmatic"
            className="text-sm text-engage-primary hover:text-engage-primary/80 font-medium"
          >
            Full Settings →
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {/* Identity Mode */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-2">Identity Mode</p>
            {settings?.identity_mode ? (
              <StreamModeIndicator mode={settings.identity_mode} compact />
            ) : (
              <span className="text-sm text-gray-600">Self-Directed (default)</span>
            )}
          </div>

          {/* Active Signals */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-2">Active Signals</p>
            <div className="flex flex-wrap gap-1">
              {settings?.signal_toggles?.epistemic !== false && (
                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                  Epistemic
                </span>
              )}
              {settings?.signal_toggles?.commitment !== false && (
                <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                  Commitment
                </span>
              )}
              {settings?.signal_toggles?.fused !== false && (
                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                  Fused
                </span>
              )}
            </div>
          </div>

          {/* Calibration */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-2">Calibration Weights</p>
            <div className="space-y-1 text-xs text-gray-600">
              <p>Hedging: <span className="font-mono">{settings?.calibration_weights?.hedging_weight?.toFixed(1) ?? '1.0'}x</span></p>
              <p>Certainty: <span className="font-mono">{settings?.calibration_weights?.certainty_weight?.toFixed(1) ?? '1.0'}x</span></p>
              <p>Commitment: <span className="font-mono">{settings?.calibration_weights?.commitment_weight?.toFixed(1) ?? '1.0'}x</span></p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <Link
          to="/tenant/engage/pragmatic"
          className="px-4 py-2 bg-engage-primary text-white rounded-lg text-sm font-medium hover:bg-engage-primary/90 transition-colors"
        >
          Configure Pragmatic Intelligence
        </Link>
        <Link
          to="/tenant/engage/coach"
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
        >
          Open Coach Lens
        </Link>
      </div>
    </div>
  )
}

function GeneralTab() {
  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Real-Time Features</h3>
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Condition Detection</p>
              <p className="text-sm text-gray-500">
                Automatically detect customer emotional states
              </p>
            </div>
            <input type="checkbox" defaultChecked className="toggle" />
          </label>
          <label className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Guidance Generation</p>
              <p className="text-sm text-gray-500">
                Generate real-time agent guidance
              </p>
            </div>
            <input type="checkbox" defaultChecked className="toggle" />
          </label>
          <label className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Auto Persona Refresh</p>
              <p className="text-sm text-gray-500">
                Refresh personas when significant changes detected
              </p>
            </div>
            <input type="checkbox" defaultChecked className="toggle" />
          </label>
        </div>
      </div>

      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Default Persona Settings</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Output Format
            </label>
            <select className="w-full px-4 py-2 border border-gray-200 rounded-lg">
              <option value="json">JSON</option>
              <option value="yaml">YAML</option>
              <option value="prompt">Natural Language Prompt</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Persona Expiration
            </label>
            <select className="w-full px-4 py-2 border border-gray-200 rounded-lg">
              <option value="24">24 hours</option>
              <option value="72">72 hours</option>
              <option value="168">1 week</option>
              <option value="720">30 days</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 mb-4">API Configuration</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              WebSocket Endpoint
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono">
                wss://api.personicai.com/api/engage/v1/realtime/
              </code>
              <button className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                Copy
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Use this endpoint to connect real-time sessions. See{' '}
            <a href="/developers/engage/websocket" className="text-engage-primary hover:underline">
              WebSocket documentation
            </a>{' '}
            for details.
          </p>
        </div>
      </div>
    </div>
  )
}

function PersonaEngineTab() {
  const { data: config, isLoading: configLoading } = usePersonaEngineConfig()
  const { data: engines } = usePersonaEngineEngines()
  const updateConfig = useUpdatePersonaEngineConfig()

  const [engineSelection, setEngineSelection] = useState<string>(config?.engine_selection ?? 'auto')
  const [activeModifiers, setActiveModifiers] = useState<ModifierType[]>(
    (config?.modifier_config?.modifiers as ModifierType[]) ?? []
  )
  const [thresholds, setThresholds] = useState<Record<string, number>>(
    config?.confidence_thresholds ?? {}
  )
  const [reversionMessages, setReversionMessages] = useState(
    config?.state_layer_reversion_messages ?? 5
  )
  const [reversionMinutes, setReversionMinutes] = useState(
    config?.state_layer_reversion_minutes ?? 10
  )
  const [injectionProfile, setInjectionProfile] = useState<InjectionProfile>(
    (config?.injection_profile as InjectionProfile) ?? 'core'
  )
  const [hasChanges, setHasChanges] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Sync from config when loaded
  useEffect(() => {
    if (config) {
      setEngineSelection(config.engine_selection ?? 'auto')
      setActiveModifiers((config.modifier_config?.modifiers as ModifierType[]) ?? [])
      setThresholds(config.confidence_thresholds ?? {})
      setReversionMessages(config.state_layer_reversion_messages ?? 5)
      setReversionMinutes(config.state_layer_reversion_minutes ?? 10)
      setInjectionProfile((config.injection_profile as InjectionProfile) ?? 'core')
    }
  }, [config])

  // Track changes
  useEffect(() => {
    if (config) {
      const changed =
        engineSelection !== (config.engine_selection ?? 'auto') ||
        JSON.stringify(activeModifiers) !== JSON.stringify((config.modifier_config?.modifiers ?? [])) ||
        JSON.stringify(thresholds) !== JSON.stringify(config.confidence_thresholds ?? {}) ||
        reversionMessages !== (config.state_layer_reversion_messages ?? 5) ||
        reversionMinutes !== (config.state_layer_reversion_minutes ?? 10) ||
        injectionProfile !== (config.injection_profile ?? 'core')
      setHasChanges(changed)
    }
  }, [config, engineSelection, activeModifiers, thresholds, reversionMessages, reversionMinutes, injectionProfile])

  const handleSave = async () => {
    try {
      await updateConfig.mutateAsync({
        engine_selection: engineSelection,
        modifier_config: { modifiers: activeModifiers },
        confidence_thresholds: thresholds,
        state_layer_reversion_messages: reversionMessages,
        state_layer_reversion_minutes: reversionMinutes,
        injection_profile: injectionProfile,
      })
      setSaveSuccess(true)
      setHasChanges(false)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (error) {
      console.error('Failed to save Persona Engine config:', error)
    }
  }

  const handleReset = () => {
    if (config) {
      setEngineSelection(config.engine_selection ?? 'auto')
      setActiveModifiers((config.modifier_config?.modifiers as ModifierType[]) ?? [])
      setThresholds(config.confidence_thresholds ?? {})
      setReversionMessages(config.state_layer_reversion_messages ?? 5)
      setReversionMinutes(config.state_layer_reversion_minutes ?? 10)
      setInjectionProfile((config.injection_profile as InjectionProfile) ?? 'core')
      setHasChanges(false)
    }
  }

  if (configLoading) {
    return <div className="text-center py-12 text-gray-500">Loading persona engine configuration...</div>
  }

  return (
    <div className="space-y-6">
      <p className="text-gray-600">
        Configure the Counterparty Persona Engine — select engines, modifiers, and confidence thresholds.
      </p>

      {saveSuccess && (
        <div className="flex items-center gap-2 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700">
          <span>Configuration saved successfully!</span>
        </div>
      )}

      {/* Engine Selection */}
      <div className="card p-6">
        <EngineSelector
          value={engineSelection}
          onChange={setEngineSelection}
          engines={engines}
          productLens="engage"
        />
      </div>

      {/* Modifier Configuration */}
      <div className="card p-6">
        <ModifierPanel
          activeModifiers={activeModifiers}
          onChange={setActiveModifiers}
          primaryEngine={engineSelection as EngineType}
        />
      </div>

      {/* Confidence Thresholds */}
      <div className="card p-6">
        <ThresholdSliders
          thresholds={thresholds}
          onChange={setThresholds}
        />
      </div>

      {/* State Layer Reversion */}
      <div className="card p-6">
        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
          State Layer Reversion
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          How quickly persona state-layer adjustments revert to baseline after a conversation.
        </p>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reversion after N messages
            </label>
            <input
              type="number"
              min={1}
              max={50}
              value={reversionMessages}
              onChange={(e) => setReversionMessages(Math.max(1, parseInt(e.target.value) || 5))}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-personic-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reversion after N minutes
            </label>
            <input
              type="number"
              min={1}
              max={120}
              value={reversionMinutes}
              onChange={(e) => setReversionMinutes(Math.max(1, parseInt(e.target.value) || 10))}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-personic-500"
            />
          </div>
        </div>
      </div>

      {/* Injection Profile */}
      <div className="card p-6">
        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
          Injection Profile
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Controls how much persona data is injected into LLM prompts.
        </p>
        <div className="space-y-3">
          {[
            { value: 'core' as InjectionProfile, label: 'Core', description: 'Top 6 dimensions + triggers + floor — minimal token usage' },
            { value: 'full' as InjectionProfile, label: 'Full', description: 'All dimensions, raw computation, and extended metadata' },
          ].map((option) => (
            <label
              key={option.value}
              className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                injectionProfile === option.value
                  ? 'border-personic-500 bg-personic-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="injection-profile"
                value={option.value}
                checked={injectionProfile === option.value}
                onChange={() => setInjectionProfile(option.value)}
                className="mt-1 h-4 w-4 text-personic-600 focus:ring-personic-500 border-gray-300"
              />
              <div>
                <span className="font-medium text-gray-900">{option.label}</span>
                <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Save / Reset */}
      <div className="flex items-center justify-end gap-4 pt-2">
        <button
          onClick={handleReset}
          disabled={!hasChanges}
          className="px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Reset
        </button>
        <button
          onClick={handleSave}
          disabled={!hasChanges || updateConfig.isPending}
          className="px-6 py-2 bg-engage-primary text-white rounded-lg hover:bg-engage-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {updateConfig.isPending ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  )
}
